import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ConfigProvider, DatePicker, Form, Input, InputNumber, Popover, Select, Spin, Upload } from 'antd';
import { DownOutlined, FilePdfOutlined, FileImageOutlined, HistoryOutlined, InboxOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import useAxiosClient from '../axios-client.js';
import { useStateContext } from '../contexts/ContextProvider.jsx';
import { useFabricTypes } from '../hooks/useFabricTypes.jsx';
import { useFabrics } from '../hooks/useFabrics.jsx';
import CustomerPicker from '../components/Customer/CustomerPicker.jsx';
// The forms here are the order form's: same Band/grid primitives, same antd
// chrome, so a drawer reads like the Create Order page rather than its own dialect.
import { Band, Mono } from '../components/Order/orderFormShared.jsx';
import '../components/Order/orderForm.css';
import '../components/Stock/fabricForm.css';
import {
  FilterChip, LoadingRow, MenuList, PageBtn, PanelDrawer, StatTile, SummaryStat,
  filterBtn, ghostBtn, miniLabel, primaryBtn, rowBtn, sheetCancelBtn, sheetOkBtn,
} from '../components/Stock/stockChrome.jsx';

// Same shell palette Ready Stock uses, re-declared on this screen's root so a
// nested ConfigProvider's portalled popovers and drawers read the same values.
import { PRIMARY, hexToRgba, shellAntdTheme, shellVars, STATUS } from '../shellTokens';

const PAGE_SIZE_OPTIONS = [15, 30, 50];
const PAYMENT_METHODS = ['Cash', 'Bank', 'Couriar'];

/** Challan scans are documents, so this matches the server's 10 MB cap. */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Ledger vocabulary — label and tint, in one place. */
const MOVEMENT_META = {
  RECEIVE_IN: { label: 'RECEIVED', tint: STATUS.ok },
  SALE_OUT: { label: 'SOLD', tint: STATUS.errorSoft },
  PRODUCTION_OUT: { label: 'PRODUCTION', tint: 'var(--activeText)', bg: 'var(--activeBg)' },
  ADJUSTMENT: { label: 'ADJUSTMENT', tint: STATUS.warn },
};

const movementMeta = (type) => MOVEMENT_META[type] || { label: type || '—', tint: 'var(--muted)' };

const fmtKg = (n) => `${Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtMoney = (n) => Number(n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const nameOf = (list, id) => list.find((x) => x.id === id)?.name;

const stockKey = (fabricTypeId, fabricColorId) => `${fabricTypeId}:${fabricColorId}`;

/**
 * The fabric+colour pairs a sheet asks more of than the store holds.
 *
 * Demand is summed per pair before it is compared, because the service applies
 * lines one after another against a running balance: two lines of 8 kg against
 * 10 kg on hand fail on the second, so checking each line against the full
 * balance on its own would wave through a pair the store cannot cover.
 *
 * A pair with no balance row has never been stocked, and counts as short the
 * moment it is picked — there is nothing for it to draw on.
 */
const shortfalls = (lines, balances) => {
  const wanted = new Map();
  (lines || []).forEach((line) => {
    if (!line?.fabricTypeId || !line?.fabricColorId) return;
    const key = stockKey(line.fabricTypeId, line.fabricColorId);
    const at = wanted.get(key) || { weight: 0, rolls: 0 };
    wanted.set(key, {
      weight: at.weight + (Number(line.weight) || 0),
      rolls: at.rolls + (Number(line.rolls) || 0),
    });
  });

  const short = new Map();
  wanted.forEach((want, key) => {
    const held = balances.get(key) || { weight: 0, rolls: 0 };
    const stocked = balances.has(key) && held.weight > 0;
    const shortWeight = Math.round((want.weight - held.weight) * 100) / 100;
    const shortRolls = want.rolls - held.rolls;
    if (!stocked || shortWeight > 0 || shortRolls > 0) {
      short.set(key, { ...held, stocked, shortWeight, shortRolls });
    }
  });
  return short;
};

/**
 * What the lines come to at their own rates, or null if any one of them is
 * unpriced. All-or-nothing on purpose: a total that adds up some lines and not
 * others is worse than no total, and the invoice only prints the rate columns
 * when it can print them for every line.
 */
const pricedTotal = (lines) => {
  const rows = lines || [];
  if (!rows.length) return null;

  let total = 0;
  for (const line of rows) {
    const rate = Number(line?.rate);
    const weight = Number(line?.weight);
    if (line?.rate === null || line?.rate === undefined || line?.rate === '' || Number.isNaN(rate) || Number.isNaN(weight)) {
      return null;
    }
    total += weight * rate;
  }
  return Math.round(total * 100) / 100;
};

/** window width < 768, matching Ready Stock's card-list breakpoint. */
const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
};

const GRID = 'minmax(0,2fr) minmax(0,1.6fr) minmax(0,1.2fr) 120px 90px 150px';

/**
 * The raw-fabric store.
 *
 * Fabric comes in against a supplier challan (weighed in kg, counted in rolls,
 * with the challan scanned) and leaves two ways: sold on to an outside party,
 * or issued to our own production floor. Distinct from Ready Stock, which
 * counts finished units.
 */
function FabricStore() {
  const axiosClient = useAxiosClient();
  const { permissions, darkMode } = useStateContext();
  const isMobile = useIsMobile();

  const canManage = (permissions || []).includes('MANAGE_FABRIC_STOCK');

  const { fabricTypes } = useFabricTypes();
  // Fabric colours are paginated 20 at a time, so the picker searches on the
  // server and pages in on scroll — the same contract the order form's colour
  // picker uses. Holding one local page would silently hide every colour past
  // the twentieth.
  const { fabrics: fabricColors, fabricsLoading, fetchFabrics, loadMore, hasMore } = useFabrics();
  const colorSearch = useRef(null);

  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ weight: 0, rolls: 0 });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortField, setSortField] = useState('onHandWeight');
  const [sortOrder, setSortOrder] = useState('desc');
  const [stats, setStats] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [fabricTypeId, setFabricTypeId] = useState(null);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [openFilter, setOpenFilter] = useState(null);

  // Per-fabric history drawer
  const [historyStock, setHistoryStock] = useState(null);
  const [movements, setMovements] = useState([]);
  const [historySummary, setHistorySummary] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Store-wide ledger drawer
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  // Receive / Sell / Consume sheet. One drawer, three modes, one antd Form —
  // so every field validates inline the way the order form's do.
  const [sheet, setSheet] = useState(null); // 'receive' | 'sale' | 'production'
  const [saving, setSaving] = useState(false);
  const [sheetForm] = Form.useForm();
  const [sheetInit, setSheetInit] = useState({});
  // Bumped on every open so the Form remounts even when the same mode is
  // reopened from a different row — otherwise initialValues would not reapply
  // and the drawer would still show the previous row's fabric.
  const [sheetSeq, setSheetSeq] = useState(0);
  // Mirror of the form's values, kept for the totals and the challan variance;
  // reading them straight off the store would need a re-render the form store
  // does not trigger on its own.
  const [values, setValues] = useState({});
  // Challan scans stay in React state: the Form.List prefix would rename them,
  // and the bytes are uploaded on submit, not held in the form store.
  const [files, setFiles] = useState([]);
  // The colour a sheet was pre-filled with, so its option survives a search
  // that pages it out of the list.
  const [seedColors, setSeedColors] = useState([]);
  // On-hand per fabric+colour, loaded when a sell or issue sheet opens so the
  // form can say what is available before anything is submitted. `null` means
  // not answered yet — distinct from an empty map, which would mean every
  // fabric is empty and would flag a pre-filled line red before the data lands.
  const [balances, setBalances] = useState(null);

  const fetchStock = useCallback(async (overrides = {}) => {
    const p = overrides.page ?? page;
    const search = 'search' in overrides ? overrides.search : searchText;
    const fabric = 'fabricTypeId' in overrides ? overrides.fabricTypeId : fabricTypeId;
    const stocked = 'inStockOnly' in overrides ? overrides.inStockOnly : inStockOnly;
    const size = overrides.pageSize ?? pageSize;
    const field = overrides.sortField ?? sortField;
    const order = overrides.sortOrder ?? sortOrder;

    setLoading(true);
    try {
      const res = await axiosClient.get('/fabricStock/index', {
        params: {
          page: p, perPage: size, sortField: field, sortOrder: order,
          search: search || undefined,
          fabricTypeId: fabric ?? undefined,
          inStockOnly: stocked ? 1 : 0,
        },
      });
      setStocks(res.data.data || []);
      setTotal(res.data.meta?.total || 0);
      setSummary(res.data.summary || { weight: 0, rolls: 0 });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load fabric stock');
    } finally {
      setLoading(false);
    }
  }, [axiosClient, page, pageSize, searchText, fabricTypeId, inStockOnly, sortField, sortOrder]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axiosClient.get('/fabricStock/stats');
      setStats(res.data);
    } catch {
      // The tiles are a nicety; the table below still stands without them.
    }
  }, [axiosClient]);

  useEffect(() => { fetchStock(); }, [fetchStock]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => () => { if (colorSearch.current) clearTimeout(colorSearch.current); }, []);

  const applyFilter = (patch) => {
    if ('search' in patch) setSearchText(patch.search);
    if ('fabricTypeId' in patch) setFabricTypeId(patch.fabricTypeId);
    if ('inStockOnly' in patch) setInStockOnly(patch.inStockOnly);
    setPage(1);
    setOpenFilter(null);
    fetchStock({ ...patch, page: 1 });
  };

  const toggleSort = (field) => {
    const order = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(order);
    fetchStock({ sortField: field, sortOrder: order });
  };

  const openHistory = async (stock) => {
    setHistoryStock(stock);
    setHistoryLoading(true);
    try {
      const res = await axiosClient.get(`/fabricStock/movements/${stock.id}`);
      setMovements(res.data.data || []);
      setHistorySummary(res.data.summary || null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openLedger = async () => {
    setLedgerOpen(true);
    setLedgerLoading(true);
    try {
      const res = await axiosClient.get('/fabricStock/movements', { params: { perPage: 100 } });
      setLedger(res.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load the ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  /**
   * Balances, keyed by fabric+colour. Refetched every time an outflow sheet
   * opens rather than cached with the page: what is on hand moves under you,
   * and a stale "12 kg available" is worse than none.
   */
  const loadBalances = useCallback(async () => {
    try {
      const res = await axiosClient.get('/fabricStock/balances');
      setBalances(new Map(
        (res.data || []).map((b) => [
          `${b.fabricTypeId}:${b.fabricColorId}`,
          { weight: Number(b.onHandWeight) || 0, rolls: Number(b.onHandRolls) || 0 },
        ]),
      ));
    } catch {
      // Left unanswered rather than empty: claiming everything is out of stock
      // would be worse than saying nothing. The service's own guard still
      // refuses an overdraw on submit, so this costs the warning, not the
      // safety.
      setBalances(null);
    }
  }, [axiosClient]);

  // The picked buyer's running account. Fetched when they are chosen so the
  // form can say what they already owe before this document is added to it.
  const [account, setAccount] = useState(null);
  const customerId = values.customer?.id ?? null;

  useEffect(() => {
    if (!customerId || sheet !== 'sale') {
      setAccount(null);
      return undefined;
    }

    let live = true;
    axiosClient.get('/fabricStock/payments/balance', { params: { customerId } })
      .then((res) => { if (live) setAccount(res.data.balance || null); })
      .catch(() => { if (live) setAccount(null); });

    return () => { live = false; };
  }, [axiosClient, customerId, sheet]);

  const openSheet = (mode, stock = null) => {
    // Opening from a row pre-fills that fabric — the common case is acting on
    // the row you were just looking at. The Form is keyed on `sheet`, so these
    // land as initialValues on a fresh mount rather than a setFieldsValue into
    // a form that has not rendered yet.
    const init = {
      date: dayjs(),
      paymentMethod: 'Cash',
      lines: [stock
        ? { fabricTypeId: stock.fabricTypeId, fabricColorId: stock.fabricColorId }
        : {}],
    };
    setSheetInit(init);
    setValues(init);
    setFiles([]);
    setSeedColors(stock?.fabricColorId ? [{ id: stock.fabricColorId, name: stock.fabricColor }] : []);
    setSheetSeq((n) => n + 1);
    setSheet(mode);
    // Only the two sheets that take fabric away are constrained by what is
    // there; a receipt adds to the balance whatever it currently is.
    setBalances(null);
    if (mode !== 'receive') loadBalances();
  };

  /** The paged colour list, plus whatever the sheet was opened on. */
  const colorOptions = useMemo(() => {
    const seen = new Set(fabricColors.map((c) => c.id));
    return [
      ...fabricColors.map((c) => ({ value: c.id, label: c.name })),
      ...seedColors.filter((c) => !seen.has(c.id)).map((c) => ({ value: c.id, label: c.name })),
    ];
  }, [fabricColors, seedColors]);

  /** Colour id → name, for the line cards' identity strip. */
  const colorNames = useMemo(
    () => new Map(colorOptions.map((o) => [o.value, o.label])),
    [colorOptions],
  );

  const searchColors = (term) => {
    if (colorSearch.current) clearTimeout(colorSearch.current);
    colorSearch.current = setTimeout(() => fetchFabrics(1, term), 400);
  };

  const onColorScroll = (e) => {
    const { target } = e;
    if (!fabricsLoading && hasMore && target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
      loadMore();
    }
  };

  // Form.List's add/remove change the store without going through
  // onValuesChange, so the mirror is re-read explicitly after either — without
  // this a removed line leaves every card below it labelled with its neighbour's
  // fabric until the next keystroke.
  const syncValues = () => setValues(sheetForm.getFieldsValue());

  const lines = useMemo(() => values.lines || [], [values]);
  const linesWeight = useMemo(
    () => lines.reduce((sum, l) => sum + (Number(l?.weight) || 0), 0),
    [lines],
  );
  const linesRolls = useMemo(
    () => lines.reduce((sum, l) => sum + (Number(l?.rolls) || 0), 0),
    [lines],
  );

  const isOutflow = sheet === 'sale' || sheet === 'production';

  const shortByKey = useMemo(
    () => (isOutflow && balances ? shortfalls(lines, balances) : new Map()),
    [isOutflow, lines, balances],
  );

  // Weighed minus claimed. Null until a challan weight is entered — not the
  // same as a zero variance.
  const variance = values.chalanWeight === null || values.chalanWeight === undefined
    ? null
    : Math.round((linesWeight - Number(values.chalanWeight)) * 100) / 100;

  // Every line priced — the amount is then derived and the field is read-only.
  const linesPriced = sheet === 'sale' && pricedTotal(lines) !== null;

  /**
   * Where the buyer lands once this sale and its payment are added to what they
   * already owed. Not `amount - paid`: money settles the oldest bill first, so a
   * customer carrying 5,000 who overpays today comes out level rather than
   * showing a negative due on this document.
   */
  const balanceAfter = sheet !== 'sale' || values.amount === null || values.amount === undefined
    ? null
    : Math.round((
      (account ? account.outstanding - account.advance : 0)
      + Number(values.amount) - Number(values.paidAmount || 0)
    ) * 100) / 100;

  /** Buffer files locally; they upload on submit so a cancelled form leaves nothing behind. */
  const beforeUpload = (file) => {
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`${file.name} is over 10 MB`);
      return Upload.LIST_IGNORE;
    }
    setFiles((prev) => [...prev, file]);
    return false;
  };

  const submitSheet = async () => {
    let v;
    try {
      v = await sheetForm.validateFields();
    } catch {
      return; // antd has already marked and scrolled to the offending field
    }

    // Recomputed from the validated values rather than trusting the mirror, and
    // named per fabric — the service refuses an overdraw too, but only after a
    // round trip and only about the first line it trips on.
    if (isOutflow && balances) {
      const short = shortfalls(v.lines, balances);
      if (short.size) {
        const named = [...short.keys()].map((key) => {
          const [typeId, colorId] = key.split(':').map(Number);
          return [nameOf(fabricTypes, typeId), colorNames.get(colorId)].filter(Boolean).join(' · ');
        });
        toast.error(`Not enough stock for ${named.join(', ')}.`);
        return;
      }
    }

    const payloadLines = (v.lines || []).map((l) => ({
      fabricTypeId: l.fabricTypeId,
      fabricColorId: l.fabricColorId,
      weight: Number(l.weight),
      rolls: Number(l.rolls) || 0,
      // Only a sale prices its lines, and a blank rate stays null rather than
      // becoming a zero the invoice would print as free fabric.
      ...(sheet === 'sale' && l.rate !== null && l.rate !== undefined && l.rate !== ''
        ? { rate: Number(l.rate) }
        : {}),
    }));

    setSaving(true);
    try {
      if (sheet === 'receive') {
        // Upload the challan scans first: the receipt stores their metadata, so
        // the bytes have to be on S3 before it is written.
        let uploaded = [];
        if (files.length) {
          const form = new FormData();
          files.forEach((f) => form.append('files[]', f));
          const up = await axiosClient.post('/fabricStock/receipts/chalan', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          uploaded = up.data || [];
        }

        await axiosClient.post('/fabricStock/receipts', {
          receivedOn: v.date.format('YYYY-MM-DD'),
          driver: v.driver || null,
          chalanWeight: v.chalanWeight ?? null,
          note: v.note || null,
          lines: payloadLines,
          files: uploaded,
        });
        toast.success('Fabric received.');
      } else {
        await axiosClient.post('/fabricStock/outflows', {
          purpose: sheet === 'sale' ? 'SALE' : 'PRODUCTION',
          happenedOn: v.date.format('YYYY-MM-DD'),
          note: v.note || null,
          lines: payloadLines,
          ...(sheet === 'sale' ? {
            customerId: v.customer.id,
            amount: v.amount ?? null,
            paidAmount: v.paidAmount ?? null,
            paymentMethod: v.paymentMethod || null,
          } : {}),
        });
        toast.success(sheet === 'sale' ? 'Fabric sold.' : 'Fabric issued to production.');
      }

      setSheet(null);
      fetchStock();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const rootStyle = shellVars(darkMode);

  const fabricName = useMemo(
    () => nameOf(fabricTypes, fabricTypeId),
    [fabricTypeId, fabricTypes],
  );

  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const showEmpty = !loading && stocks.length === 0;
  const hasActiveFilters = fabricTypeId !== null;

  const sheetTitle = sheet === 'receive' ? 'Receive fabric'
    : sheet === 'sale' ? 'Sell fabric'
    : 'Issue to production';
  const sheetSubtitle = sheet === 'receive' ? 'Weighed in against a supplier challan'
    : sheet === 'sale' ? 'Raw fabric sold on to a customer'
    : sheet === 'payment' ? 'Money in against a customer\u2019s account'
    : 'Raw fabric issued to our own production floor';

  return (
    <div className="et-fab" style={rootStyle}>
      <style>{`
        .et-fab{font-family:Outfit,system-ui,sans-serif;color:var(--text);display:flex;flex-direction:column;gap:14px;height:100%;min-height:0}
        .et-fab__row:hover{background:var(--hover)}
        .et-fab__ghost:hover{background:var(--hover)}
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em' }}>Fabric Store</div>
          <div style={{ font: '400 13px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
            Raw fabric on hand, in kg and rolls, per fabric and colour.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="et-fab__ghost" onClick={openLedger} style={ghostBtn}>
            <HistoryOutlined style={{ color: 'var(--muted)', fontSize: 12 }} /> All movements
          </button>
          {canManage && (
            <>
              <button type="button" className="et-fab__ghost" onClick={() => openSheet('production')} style={ghostBtn}>
                Issue to production
              </button>
              <button type="button" className="et-fab__ghost" onClick={() => openSheet('sale')} style={ghostBtn}>
                Sell
              </button>
              <button type="button" onClick={() => openSheet('receive')} style={primaryBtn}>
                + Receive fabric
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 14 }}>
          <StatTile
            icon="▦" tint={PRIMARY} label="Fabric on hand"
            value={`${fmtKg(stats.weightOnHand)} kg`}
            foot={`${stats.rollsOnHand.toLocaleString()} rolls · ${stats.fabricsWithStock} of ${stats.totalFabrics} fabrics`}
          />
          <StatTile
            icon="↓" tint={STATUS.ok} label={`Received · ${stats.windowDays}d`}
            value={`${fmtKg(stats.receivedWeight)} kg`}
            foot="weighed in against challans"
          />
          <StatTile
            icon="↗" tint={STATUS.errorSoft} label={`Sold · ${stats.windowDays}d`}
            value={`${fmtKg(stats.soldWeight)} kg`}
            foot="to customers"
          />
          <StatTile
            icon="⚙" tint="#a78bfa" label={`To production · ${stats.windowDays}d`}
            value={`${fmtKg(stats.consumedWeight)} kg`}
            foot="consumed in house"
          />
        </div>
      )}

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

          {/* Toolbar */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Fabrics</div>
              <div style={{ font: '600 11.5px monospace', color: 'var(--dim)', padding: '2px 7px', borderRadius: 9, background: 'var(--surface2)' }}>{total}</div>
            </div>
            <div className="et-toolbar">
              <Input
                allowClear
                placeholder="Search fabric or colour"
                prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
                style={{ width: isMobile ? '100%' : 280 }}
                value={searchInput}
                // Emptying the box re-runs the query — antd 5.4's allowClear has
                // no onClear callback, it just fires onChange with ''.
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  if (e.target.value === '' && searchText !== '') applyFilter({ search: '' });
                }}
                onPressEnter={() => applyFilter({ search: searchInput })}
              />
              <Popover
                trigger="click"
                open={openFilter === 'fabric'}
                onOpenChange={(o) => setOpenFilter(o ? 'fabric' : null)}
                content={
                  <MenuList
                    darkMode={darkMode}
                    items={[{ key: null, label: 'All fabrics' }, ...fabricTypes.map((f) => ({ key: f.id, label: f.name }))]}
                    active={fabricTypeId}
                    onSelect={(v) => applyFilter({ fabricTypeId: v })}
                  />
                }
              >
                <button type="button" style={filterBtn}>
                  {fabricName || 'Fabric'} <DownOutlined style={{ fontSize: 9, color: 'var(--muted)' }} />
                </button>
              </Popover>
              <div
                role="button" tabIndex={0}
                onClick={() => applyFilter({ inStockOnly: !inStockOnly })}
                onKeyDown={(e) => { if (e.key === 'Enter') applyFilter({ inStockOnly: !inStockOnly }); }}
                style={{
                  height: 32, padding: '0 11px', borderRadius: 8,
                  border: `1px solid ${inStockOnly ? hexToRgba(PRIMARY, .35) : 'var(--border)'}`,
                  background: inStockOnly ? 'var(--activeBg)' : 'var(--surface2)',
                  display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer',
                }}
              >
                <span style={{ width: 26, height: 15, borderRadius: 8, background: inStockOnly ? PRIMARY : 'var(--borderStrong)', display: 'flex', alignItems: 'center', justifyContent: inStockOnly ? 'flex-end' : 'flex-start', padding: '0 2px' }}>
                  <span style={{ width: 11, height: 11, borderRadius: 6, background: '#fff' }} />
                </span>
                <span style={{ font: '600 12px Outfit, sans-serif', color: inStockOnly ? 'var(--activeText)' : 'var(--muted)' }}>In stock only</span>
              </div>
            </div>
          </div>

          {/* Filter chips */}
          {hasActiveFilters && (
            <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ ...miniLabel, marginRight: 2 }}>Filters</span>
              <FilterChip label="Fabric" value={fabricName} onClear={() => applyFilter({ fabricTypeId: null })} />
            </div>
          )}

          {/* Desktop table */}
          {!isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <div style={{ minWidth: 880 }}>
                <div style={{ display: 'grid', gridTemplateColumns: GRID, background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  {[
                    { key: 'fabric', label: 'Fabric', pad: '9px 16px' },
                    { key: 'color', label: 'Colour' },
                    { key: 'lastMovement', label: 'Last movement', sortable: true },
                    { key: 'onHandWeight', label: 'On hand (kg)', sortable: true, align: 'flex-end' },
                    { key: 'onHandRolls', label: 'Rolls', sortable: true, align: 'flex-end' },
                    { key: 'action', label: 'Action', align: 'flex-end', pad: '9px 16px 9px 8px' },
                  ].map((h) => (
                    <div
                      key={h.key}
                      onClick={h.sortable ? () => toggleSort(h.key) : undefined}
                      style={{
                        padding: h.pad || '9px 8px', display: 'flex', alignItems: 'center', gap: 5,
                        justifyContent: h.align || 'flex-start',
                        font: '700 11.5px Outfit, sans-serif',
                        color: sortField === h.key ? 'var(--activeText)' : 'var(--muted)',
                        letterSpacing: '.05em', textTransform: 'uppercase',
                        cursor: h.sortable ? 'pointer' : 'default',
                      }}
                    >
                      {h.label}
                      {h.sortable && sortField === h.key && <span style={{ color: PRIMARY, fontSize: 9 }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>}
                    </div>
                  ))}
                </div>

                {loading && <LoadingRow />}
                {showEmpty && <EmptyState search={searchText} inStockOnly={inStockOnly} onShowAll={() => applyFilter({ inStockOnly: false })} />}

                {!loading && stocks.map((s) => {
                  const meta = movementMeta(s.lastMovementType);
                  return (
                    <div key={s.id} className="et-fab__row" style={{ display: 'grid', gridTemplateColumns: GRID, borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                      <div style={{ padding: '11px 16px', font: '600 13px Outfit, sans-serif' }}>{s.fabricType || '—'}</div>
                      <div style={{ padding: '11px 8px', font: '400 12.5px Outfit, sans-serif', color: 'var(--text)' }}>{s.fabricColor || '—'}</div>
                      <div style={{ padding: '11px 8px', display: 'flex', alignItems: 'center', gap: 7 }}>
                        {s.lastMovementType
                          ? (
                            <>
                              <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '3px 7px', borderRadius: 5 }}>{meta.label}</span>
                              <span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                                {s.daysSinceMovement === 0 ? 'today' : `${s.daysSinceMovement}d`}
                              </span>
                            </>
                          )
                          : <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>never moved</span>}
                      </div>
                      <div style={{ padding: '11px 8px', textAlign: 'right', font: '700 13px monospace', color: s.onHandWeight > 0 ? 'var(--text)' : 'var(--dim)' }}>
                        {fmtKg(s.onHandWeight)}
                      </div>
                      <div style={{ padding: '11px 8px', textAlign: 'right', font: '600 12.5px monospace', color: 'var(--muted)' }}>
                        {s.onHandRolls}
                      </div>
                      <div style={{ padding: '11px 16px 11px 8px', display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
                        <button type="button" className="et-fab__ghost" onClick={() => openHistory(s)} style={rowBtn}>History</button>
                        {canManage && (
                          <button type="button" className="et-fab__ghost" onClick={() => openSheet('production', s)} style={rowBtn}>Issue</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mobile cards */}
          {isMobile && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {loading && <LoadingRow />}
              {showEmpty && <EmptyState search={searchText} inStockOnly={inStockOnly} onShowAll={() => applyFilter({ inStockOnly: false })} />}
              {!loading && stocks.map((s) => (
                <div key={s.id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--surface2)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>{s.fabricType || '—'}</div>
                      <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>{s.fabricColor || '—'}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ font: '700 15px monospace' }}>{fmtKg(s.onHandWeight)} kg</div>
                      <div style={{ font: '600 11.5px monospace', color: 'var(--muted)' }}>{s.onHandRolls} rolls</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 7, marginTop: 10 }}>
                    <button type="button" onClick={() => openHistory(s)} style={rowBtn}>History</button>
                    {canManage && <button type="button" onClick={() => openSheet('production', s)} style={rowBtn}>Issue</button>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div style={{ padding: '11px 18px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
              {total} fabric{total === 1 ? '' : 's'} · <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{fmtKg(summary.weight)} kg</span> · {summary.rolls} rolls
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Select
                size="small"
                value={pageSize}
                style={{ width: 92 }}
                onChange={(v) => { setPageSize(v); setPage(1); fetchStock({ pageSize: v, page: 1 }); }}
                options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
              />
              <PageBtn disabled={page <= 1} onClick={() => { setPage(page - 1); fetchStock({ page: page - 1 }); }}>‹</PageBtn>
              <span style={{ font: '600 12px monospace', color: 'var(--muted)' }}>{page} / {lastPage}</span>
              <PageBtn disabled={page >= lastPage} onClick={() => { setPage(page + 1); fetchStock({ page: page + 1 }); }}>›</PageBtn>
            </div>
          </div>
        </div>

        {/* Per-fabric history */}
        <PanelDrawer
          open={!!historyStock}
          onClose={() => setHistoryStock(null)}
          darkMode={darkMode}
          width={isMobile ? '100%' : 520}
          title="History"
          subtitle={historyStock ? [historyStock.fabricType, historyStock.fabricColor].filter(Boolean).join(' · ') : ''}
          bodyPadding={18}
        >
          {historySummary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, paddingBottom: 14, borderBottom: '1px solid var(--border)', marginBottom: 12 }}>
              <SummaryStat label="On hand" value={fmtKg(historySummary.onHandWeight)} tint="var(--text)" />
              <SummaryStat label={`In · ${historySummary.windowDays}d`} value={fmtKg(historySummary.in)} tint={STATUS.ok} />
              <SummaryStat label={`Out · ${historySummary.windowDays}d`} value={fmtKg(historySummary.out)} tint={STATUS.errorSoft} />
            </div>
          )}
          {historyLoading ? <LoadingRow /> : <MovementList items={movements} />}
        </PanelDrawer>

        {/* Store-wide ledger */}
        <PanelDrawer
          open={ledgerOpen}
          onClose={() => setLedgerOpen(false)}
          darkMode={darkMode}
          width={isMobile ? '100%' : 560}
          title="All fabric movements"
          subtitle="The 100 most recent entries, store-wide."
          bodyPadding={18}
        >
          {ledgerLoading ? <LoadingRow /> : <MovementList items={ledger} showFabric />}
        </PanelDrawer>

        {/* Receive / Sell / Consume */}
        <PanelDrawer
          open={!!sheet}
          onClose={() => setSheet(null)}
          darkMode={darkMode}
          // Wider than the read-only drawers: a fabric line is four controls
          // across, and squeezing them into 640 leaves the two pickers unreadable.
          width={isMobile ? '100%' : 760}
          title={sheetTitle}
          subtitle={sheetSubtitle}
          // Bands bring their own padding, as they do on the order form.
          bodyPadding={0}
          footer={
            <div style={{ display: 'flex', gap: 9, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setSheet(null)} style={sheetCancelBtn}>Cancel</button>
              <button type="button" onClick={submitSheet} disabled={saving} style={{ ...sheetOkBtn, opacity: saving ? .6 : 1 }}>
                {saving ? 'Saving…' : sheetTitle}
              </button>
            </div>
          }
        >
          <div className="et-orderform">
            <Form
              key={`${sheet}-${sheetSeq}`}
              form={sheetForm}
              layout="vertical"
              // "div", not `false`. antd puts `ant-form-vertical` on the element
              // it renders, and vertical layout is nothing but the CSS that
              // class selects; `component={false}` renders no element at all, so
              // every label silently fell back to sitting beside its control.
              // A div keeps the class without nesting a <form>.
              component="div"
              initialValues={sheetInit}
              onValuesChange={(changed, all) => {
                // Once every line is priced, the amount is arithmetic, not a
                // judgement — so it is computed rather than typed, and the
                // invoice's line amounts are then guaranteed to sum to it.
                // Leaving one rate blank hands the field back to the user.
                if (sheet === 'sale' && 'lines' in changed) {
                  const total = pricedTotal(all.lines);
                  if (total !== null && total !== all.amount) {
                    sheetForm.setFieldValue('amount', total);
                    setValues({ ...all, amount: total });
                    return;
                  }
                }
                setValues(all);
              }}
            >
              <Band label={sheet === 'receive' ? 'Delivery' : 'When'}>
                <div className="et-of-cols3">
                  <Form.Item
                    name="date"
                    label={sheet === 'receive' ? 'Received on' : 'Date'}
                    rules={[{ required: true, message: 'Please pick a date!' }]}
                  >
                    <DatePicker allowClear={false} style={{ width: '100%' }} format="D MMM YYYY" />
                  </Form.Item>
                  {sheet === 'receive' && (
                    <>
                      <Form.Item name="driver" label="Driver">
                        <Input placeholder="Who brought it" />
                      </Form.Item>
                      <Form.Item name="chalanWeight" label="As per challan (kg)">
                        <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
                      </Form.Item>
                    </>
                  )}
                </div>
              </Band>

              {sheet === 'sale' && (
                <Band label="Customer">
                  <Form.Item
                    name="customer"
                    rules={[{ required: true, message: 'Find the customer by phone, or add them.' }]}
                  >
                    <CustomerPicker darkMode={darkMode} />
                  </Form.Item>
                  {/* What they stand at before this document. Money is allocated
                      across the whole account, so this is the number that
                      decides whether they are actually behind. */}
                  {account && (
                    <div className="et-of-band" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10 }}>
                      <AccountFact label="Billed to date" value={fmtMoney(account.billed)} />
                      <AccountFact label="Received" value={fmtMoney(account.paid)} tint={STATUS.ok} />
                      <AccountFact
                        label={account.advance > 0.5 ? 'Advance held' : 'Outstanding'}
                        value={fmtMoney(account.advance > 0.5 ? account.advance : account.outstanding)}
                        tint={account.advance > 0.5 ? STATUS.ok : account.outstanding > 0.5 ? STATUS.warn : 'var(--muted)'}
                      />
                    </div>
                  )}
                </Band>
              )}

              <Band label="Fabric">
                <Form.List name="lines">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map((field, index) => {
                        const row = lines[field.name] || {};
                        const identity = [nameOf(fabricTypes, row.fabricTypeId), colorNames.get(row.fabricColorId)]
                          .filter(Boolean).join(' · ');
                        // Only answerable once both pickers are set; until then
                        // there is no balance to speak about.
                        const key = isOutflow && balances && row.fabricTypeId && row.fabricColorId
                          ? stockKey(row.fabricTypeId, row.fabricColorId)
                          : null;
                        const held = key ? balances.get(key) : null;
                        const short = key ? shortByKey.get(key) : null;
                        return (
                          <div key={field.key} className={`et-fab-line${short ? ' et-fab-line--short' : ''}`}>
                            {/* The line's identity and its remove control, so the
                                fields below keep every column for themselves. */}
                            <div className="et-fab-line__head">
                              <IndexChip n={index + 1} />
                              <span className="et-fab-line__title">
                                {identity || <span style={{ color: 'var(--dim)', fontWeight: 400 }}>Nothing picked yet</span>}
                              </span>
                              {/* What the store actually holds, stated as soon as
                                  a fabric is picked rather than on submit. */}
                              {key && (
                                <Mono style={{ flex: 'none', fontSize: 11.5, fontWeight: 600, color: short ? STATUS.errorSoft : 'var(--dim)' }}>
                                  {held && held.weight > 0 ? `${fmtKg(held.weight)} kg on hand` : 'not in stock'}
                                </Mono>
                              )}
                              <Mono style={{ flex: 'none', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
                                {`${fmtKg(row.weight)} kg`}
                              </Mono>
                              <button
                                type="button"
                                className="et-of-iconbtn"
                                title={fields.length > 1 ? 'Remove line' : 'A receipt needs at least one line'}
                                disabled={fields.length <= 1}
                                onClick={() => { remove(field.name); syncValues(); }}
                              >
                                ×
                              </button>
                            </div>

                            {short && (
                              <div className="et-fab-line__warn">
                                {!short.stocked
                                  ? 'Nothing on hand for this fabric — receive some before it can leave the store.'
                                  : short.shortWeight > 0
                                    ? `Only ${fmtKg(short.weight)} kg on hand — ${fmtKg(short.shortWeight)} kg short.`
                                    : `Only ${short.rolls} roll${short.rolls === 1 ? '' : 's'} on hand — ${short.shortRolls} short.`}
                              </div>
                            )}

                            <div className={`et-fab-line__body${sheet === 'sale' ? ' et-fab-line__body--priced' : ''}`}>
                              <Form.Item
                                name={[field.name, 'fabricTypeId']}
                                label="Fabric"
                                rules={[{ required: true, message: 'Pick a fabric!' }]}
                              >
                                <Select
                                  showSearch optionFilterProp="label" placeholder="Select fabric"
                                  options={fabricTypes.map((f) => ({ value: f.id, label: f.name }))}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, 'fabricColorId']}
                                label="Colour"
                                rules={[{ required: true, message: 'Pick a colour!' }]}
                              >
                                {/* Server-side search, paged in on scroll — the
                                    colour list is far longer than one page. */}
                                <Select
                                  showSearch
                                  placeholder="Search colour"
                                  defaultActiveFirstOption={false}
                                  filterOption={false}
                                  onSearch={searchColors}
                                  onPopupScroll={onColorScroll}
                                  notFoundContent={fabricsLoading ? <Spin size="small" /> : null}
                                  options={colorOptions}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, 'weight']}
                                label="Weight (kg)"
                                rules={[
                                  { required: true, message: 'Enter a weight!' },
                                  // A zero-weight line would pass `required` but
                                  // means nothing moved, and the API rejects it.
                                  { type: 'number', min: 0.01, message: 'Must be more than zero!' },
                                ]}
                              >
                                <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
                              </Form.Item>
                              <Form.Item name={[field.name, 'rolls']} label="Rolls">
                                <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                              </Form.Item>
                              {sheet === 'sale' && (
                                // Optional: the sale bills on Amount either way.
                                // What the rate buys is an invoice that shows
                                // its working line by line rather than one lump.
                                <Form.Item name={[field.name, 'rate']} label="Rate ৳/kg">
                                  <InputNumber min={0} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
                                </Form.Item>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      <div className="et-fab-lines__foot">
                        <button type="button" className="et-of-chip et-of-chip--primary" onClick={() => { add({}); syncValues(); }}>
                          + Add fabric
                        </button>
                        <Mono style={{ fontSize: 12, fontWeight: 700, color: 'var(--dim)', marginLeft: 'auto' }}>
                          {`${fields.length} line${fields.length === 1 ? '' : 's'} · ${fmtKg(linesWeight)} kg · ${linesRolls} rolls`}
                        </Mono>
                      </div>
                    </>
                  )}
                </Form.List>
              </Band>

              {sheet === 'receive' && (
                <Band label="Challan">
                  {/* The reconciliation, stated before the scans that evidence it. */}
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                      padding: '9px 12px', borderRadius: 9, marginBottom: 12,
                      background: variance === null || variance === 0 ? 'var(--surface2)' : variance > 0 ? 'rgba(52,211,153,.10)' : 'rgba(248,113,113,.10)',
                      border: `1px solid ${variance === null || variance === 0 ? 'var(--border)' : variance > 0 ? 'rgba(52,211,153,.28)' : 'rgba(248,113,113,.28)'}`,
                    }}
                  >
                    <span style={{ font: '600 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                      Weighed {fmtKg(linesWeight)} kg against {values.chalanWeight == null ? 'no challan weight' : `${fmtKg(values.chalanWeight)} kg`}
                    </span>
                    <Mono style={{
                      fontSize: 13, fontWeight: 700,
                      color: variance === null ? 'var(--dim)' : variance === 0 ? 'var(--muted)' : variance > 0 ? STATUS.ok : STATUS.errorSoft,
                    }}>
                      {variance === null ? '—' : `${variance > 0 ? '+' : ''}${fmtKg(variance)} kg`}
                    </Mono>
                  </div>

                  <Upload.Dragger
                    multiple
                    accept="image/*,application/pdf"
                    beforeUpload={beforeUpload}
                    fileList={[]}
                  >
                    <div className="et-of-drop">
                      <span
                        style={{
                          flex: 'none', width: 46, height: 46, borderRadius: 12,
                          background: 'var(--surface)', color: 'var(--muted)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                        }}
                      >
                        <InboxOutlined />
                      </span>
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ font: '600 13px Outfit, sans-serif', color: 'var(--text)' }}>
                          Drop the challan copies here
                        </span>
                        <span style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                          Images or PDFs, up to 10 MB each
                        </span>
                      </span>
                    </div>
                  </Upload.Dragger>

                  {files.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                      {files.map((f, i) => (
                        <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                          {f.type === 'application/pdf'
                            ? <FilePdfOutlined style={{ color: STATUS.errorSoft }} />
                            : <FileImageOutlined style={{ color: 'var(--muted)' }} />}
                          <span style={{ font: '400 12.5px Outfit, sans-serif', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                          <Mono style={{ fontSize: 11, color: 'var(--dim)' }}>{Math.round(f.size / 1024)} KB</Mono>
                          <button
                            type="button"
                            className="et-of-iconbtn"
                            title="Remove this scan"
                            onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </Band>
              )}

              {sheet === 'sale' && (
                <Band label="Payment">
                  <div className="et-of-cols3">
                    <Form.Item
                      name="amount"
                      label="Amount"
                      extra={linesPriced ? 'From the line rates' : undefined}
                    >
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="0" disabled={linesPriced} />
                    </Form.Item>
                    <Form.Item name="paidAmount" label="Received">
                      <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
                    </Form.Item>
                    <Form.Item name="paymentMethod" label="Method">
                      <Select options={PAYMENT_METHODS.map((m) => ({ value: m, label: m }))} />
                    </Form.Item>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                    <span style={{ font: '600 12px Outfit, sans-serif', color: 'var(--muted)' }}>
                      Account after this sale
                    </span>
                    <Mono style={{ fontSize: 13, fontWeight: 700, color: balanceAfter > 0.5 ? STATUS.warn : STATUS.ok }}>
                      {balanceAfter === null ? '—' : fmtMoney(Math.max(0, balanceAfter))}
                    </Mono>
                    {balanceAfter !== null && balanceAfter < -0.5 && (
                      <span style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                        settled, with {fmtMoney(-balanceAfter)} left on account
                      </span>
                    )}
                  </div>
                </Band>
              )}

              <Band label="Note" last>
                <Form.Item name="note">
                  <Input placeholder="Optional" maxLength={255} />
                </Form.Item>
              </Band>
            </Form>
          </div>
        </PanelDrawer>
      </ConfigProvider>
    </div>
  );
}

/** The order form's line number chip, at the fabric line's scale. */
function IndexChip({ n }) {
  return (
    <span
      style={{
        flex: 'none', width: 20, height: 20, borderRadius: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        font: '700 11px monospace', background: 'var(--surface)',
        color: 'var(--dim)', border: '1px solid var(--border)',
      }}
    >
      {n}
    </span>
  );
}

function MovementList({ items, showFabric }) {
  if (!items.length) {
    return <div style={{ padding: '28px 0', textAlign: 'center', font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)' }}>Nothing recorded yet.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {items.map((m) => {
        const meta = movementMeta(m.type);
        const negative = m.weight < 0;
        return (
          <div key={m.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 110px 80px', gap: 8, borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
            <div style={{ padding: '11px 0' }}>
              <span style={{ font: '600 10.5px monospace', color: meta.tint, background: meta.bg || `${meta.tint}24`, padding: '3px 7px', borderRadius: 5 }}>{meta.label}</span>
              {showFabric && m.fabric && (
                <div style={{ font: '600 12px Outfit, sans-serif', marginTop: 5 }}>
                  {[m.fabric.fabricType, m.fabric.fabricColor].filter(Boolean).join(' · ')}
                </div>
              )}
              {m.reason && <div style={{ font: '400 11.5px Outfit, sans-serif', color: 'var(--dim)', marginTop: 5 }}>{m.reason}</div>}
              <div style={{ font: '400 11px Outfit, sans-serif', color: 'var(--dim)', marginTop: 3 }}>{m.user || 'System'}</div>
            </div>
            <div style={{ padding: '11px 0', textAlign: 'right', font: '700 13px monospace', color: negative ? STATUS.errorSoft : STATUS.ok }}>
              {negative ? '' : '+'}{fmtKg(m.weight)} kg
              <div style={{ font: '600 11px monospace', color: 'var(--muted)' }}>{m.rolls > 0 ? '+' : ''}{m.rolls} rolls</div>
            </div>
            <div style={{ padding: '11px 0', textAlign: 'right', font: '400 12px monospace', color: 'var(--muted)' }}>
              {m.createdAt ? dayjs(m.createdAt).format('D MMM') : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** One figure from the buyer's running account, on the sale and payment sheets. */
function AccountFact({ label, value, tint }) {
  return (
    <div>
      <div style={{ font: '600 10px Outfit, sans-serif', letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--dim)' }}>{label}</div>
      <Mono style={{ fontSize: 13, fontWeight: 700, color: tint || 'var(--text)' }}>{value}</Mono>
    </div>
  );
}

function EmptyState({ search, inStockOnly, onShowAll }) {
  return (
    <div style={{ padding: '32px 18px 36px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
      <span style={{ width: 38, height: 38, borderRadius: 12, background: 'var(--surface2)', color: 'var(--dim)', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {search ? <SearchOutlined /> : '▦'}
      </span>
      <div style={{ font: '600 13.5px Outfit, sans-serif' }}>
        {search
          ? <>No fabric matches <span style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--activeText)' }}>{search}</span></>
          : 'No fabric on hand'}
      </div>
      <div style={{ font: '400 12.5px/1.5 Outfit, sans-serif', color: 'var(--muted)', maxWidth: 320 }}>
        Receive a delivery to open a balance. A fabric appears here the first time it is weighed in.
      </div>
      {inStockOnly && (
        <button type="button" onClick={onShowAll} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: PRIMARY, color: '#fff', font: '700 12.5px Outfit, sans-serif', cursor: 'pointer' }}>
          Show all fabrics
        </button>
      )}
    </div>
  );
}

export default FabricStore;
